import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Pagination,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import type {
  SearchDetail,
  SearchPeriod,
  SearchProvider,
  SearchSiteView,
} from '../../../api/src/search-metrics/search-metrics.types';
import type { RankProjectSummary } from '../api/types';
import { api } from '../api/client';
import { EditorOnly } from '../components/EditorOnly';
import { Box, Stack, Typography } from '../components/mui-system';
import {
  HistoryCounts,
  MetricsChart,
  MetricsChartLegend,
  MetricsValues,
  SourceStatus,
  metric,
  readableDate,
  sourceNames,
  type ChartMode,
} from './search-metrics-presentation';

export function MetricsDetailDialog({
  workspaceId,
  site,
  period,
  kind,
  close,
}: {
  workspaceId: string;
  site: SearchSiteView;
  period: SearchPeriod;
  kind: ChartMode;
  close(): void;
}) {
  const [propertyId, setPropertyId] = useState(site.sources[0]!.property.id);
  const [dimension, setDimension] = useState<'query' | 'page'>('query');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState<SearchDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [notice, setNotice] = useState('');
  const source = site.sources.find((s) => s.property.id === propertyId) ?? site.sources[0]!;
  const revision = `${source.lastRun?.id}:${source.lastRun?.status}:${source.lastSuccessAt}`;
  useEffect(() => {
    let active = true;
    setLoading(true);
    setDetail(null);
    setError('');
    api
      .searchDetail(workspaceId, propertyId, {
        ...period,
        dimension,
        page: String(page),
        ...(search ? { search } : {}),
      })
      .then((value) => {
        if (active) setDetail(value);
      })
      .catch((e) => {
        if (active) setError(e instanceof Error ? e.message : 'Не удалось загрузить детализацию');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [workspaceId, propertyId, period.dateFrom, period.dateTo, dimension, page, search, revision]);
  return (
    <Dialog open onClose={close} fullWidth maxWidth="lg">
      <DialogTitle>{site.siteKey}</DialogTitle>
      <DialogContent>
        <Stack gap={2}>
          {error && <Alert severity="error">{error}</Alert>}
          {notice && (
            <Alert severity="success" onClose={() => setNotice('')}>
              {notice}
            </Alert>
          )}
          <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
            <ToggleButtonGroup
              exclusive
              value={propertyId}
              size="small"
              onChange={(_, value: string | null) => {
                if (value) {
                  setPropertyId(value);
                  setPage(1);
                }
              }}
              aria-label="Источник детализации"
            >
              {site.sources.map((s) => (
                <ToggleButton value={s.property.id} key={s.property.id}>
                  {sourceNames[s.property.provider]}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Typography color="text.secondary" fontSize={13}>
              {readableDate(period.dateFrom)} — {readableDate(period.dateTo)}
            </Typography>
          </Stack>
          <Typography color="text.secondary" fontSize={12}>
            {source.property.label}
          </Typography>
          <MetricsValues value={source.current} change={source.clickChange} />
          <MetricsChart sources={[source]} period={period} kind={kind} large />
          <MetricsChartLegend providers={[source.property.provider]} kind={kind} />
          <SourceStatus source={source} />
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            gap={1}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
          >
            <Tabs
              value={dimension}
              onChange={(_, value: 'query' | 'page') => {
                setDimension(value);
                setPage(1);
                setSearch('');
              }}
            >
              <Tab value="query" label="Запросы" />
              <Tab value="page" label="Страницы" />
            </Tabs>
            <TextField
              size="small"
              label={dimension === 'query' ? 'Найти запрос' : 'Найти страницу'}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </Stack>
          {loading && <LinearProgress />}
          {detail && (
            <>
              <Alert severity="info">
                {detail.note} Данные: {readableDate(detail.coverage.firstDate)} —{' '}
                {readableDate(detail.coverage.lastDate)} ({detail.coverage.days} из{' '}
                {detail.coverage.expectedDays} дней).
              </Alert>
              {detail.history && <HistoryCounts value={detail.history} />}
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{dimension === 'query' ? 'Запрос' : 'Страница'}</TableCell>
                      <TableCell align="right">Клики</TableCell>
                      <TableCell align="right">Показы</TableCell>
                      <TableCell align="right">CTR</TableCell>
                      <TableCell align="right">Позиция</TableCell>
                      {dimension === 'query' && (
                        <EditorOnly permissions="positions.import">
                          <TableCell />
                        </EditorOnly>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.rows.map((row) => (
                      <TableRow key={row.key}>
                        <TableCell sx={{ maxWidth: 450, overflowWrap: 'anywhere' }}>
                          {row.key}
                        </TableCell>
                        {(['clicks', 'impressions', 'ctr', 'position'] as const).map((k) => (
                          <TableCell key={k} align="right" sx={{ whiteSpace: 'nowrap' }}>
                            {metric(row[k], k)}
                          </TableCell>
                        ))}
                        {dimension === 'query' && (
                          <EditorOnly permissions="positions.import">
                            <TableCell>
                              <Button size="small" onClick={() => setQuery(row.key)}>
                                В Позиции
                              </Button>
                            </TableCell>
                          </EditorOnly>
                        )}
                      </TableRow>
                    ))}
                    {!detail.rows.length && (
                      <TableRow>
                        <TableCell colSpan={6}>
                          Нет сохранённых строк за выбранный период и фильтр.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography color="text.secondary" fontSize={12}>
                  Строк: {detail.totalRows}
                </Typography>
                <Pagination
                  count={Math.max(1, Math.ceil(detail.totalRows / detail.pageSize))}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                />
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Закрыть</Button>
      </DialogActions>
      {query && (
        <AddMetricQueryDialog
          workspaceId={workspaceId}
          domain={site.siteKey}
          query={query}
          provider={source.property.provider}
          close={() => setQuery('')}
          added={() => {
            setQuery('');
            setNotice('Запрос добавлен в мониторинг позиций.');
          }}
        />
      )}
    </Dialog>
  );
}

function AddMetricQueryDialog({
  workspaceId,
  domain,
  query,
  provider,
  close,
  added,
}: {
  workspaceId: string;
  domain: string;
  query: string;
  provider: SearchProvider;
  close(): void;
  added(): void;
}) {
  const [projects, setProjects] = useState<RankProjectSummary[]>([]);
  const [projectId, setProjectId] = useState('new');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    api
      .rankDashboard(workspaceId)
      .then((value) => {
        if (active) {
          const matching = value.projects.filter((p) => p.domain === domain);
          setProjects(matching);
          setProjectId(matching[0]?.id ?? 'new');
        }
      })
      .catch((e) => {
        if (active) setError(e.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [workspaceId, domain]);
  return (
    <Dialog open onClose={busy ? undefined : close} fullWidth maxWidth="sm">
      <Box
        component="form"
        onSubmit={async (event: React.FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          setBusy(true);
          setError('');
          try {
            if (projectId === 'new')
              await api.createRankProject(workspaceId, {
                name: String(form.get('name')),
                domain,
                regionCode: String(form.get('regionCode')),
                regionName: String(form.get('regionName')),
                device: String(form.get('device')) as 'desktop' | 'mobile',
                depth: 100,
                keywords: [query],
              });
            else
              await api.addRankKeywords(
                workspaceId,
                projectId,
                [query],
                provider === 'google' ? 'gsc' : 'yandex-webmaster',
              );
            added();
          } catch (e) {
            setError(e instanceof Error ? e.message : 'Не удалось добавить запрос');
          } finally {
            setBusy(false);
          }
        }}
      >
        <DialogTitle>Добавить запрос в Позиции</DialogTitle>
        <DialogContent>
          <Stack gap={2} pt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            {loading && <LinearProgress />}
            <Typography fontWeight={700}>{query}</Typography>
            <Typography fontSize={13} color="text.secondary">
              Независимый мониторинг позиций в Яндексе. Выберите проект и его параметры проверки.
            </Typography>
            <TextField
              select
              label="Проект"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
            >
              <MenuItem value="new">Создать проект для {domain}</MenuItem>
              {projects.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name} · {p.regionName} · {p.device}
                </MenuItem>
              ))}
            </TextField>
            {projectId === 'new' && (
              <>
                <TextField
                  name="name"
                  label="Название проекта"
                  defaultValue={domain}
                  required
                  slotProps={{ htmlInput: { minLength: 2, maxLength: 80 } }}
                />
                <Stack direction="row" gap={1}>
                  <TextField
                    name="regionCode"
                    label="Код региона Яндекса"
                    defaultValue="213"
                    required
                  />
                  <TextField
                    name="regionName"
                    label="Название региона"
                    defaultValue="Москва"
                    required
                  />
                </Stack>
                <TextField select name="device" label="Устройство" defaultValue="desktop">
                  <MenuItem value="desktop">Компьютер</MenuItem>
                  <MenuItem value="mobile">Мобильный</MenuItem>
                </TextField>
                <Typography fontSize={12} color="text.secondary">
                  Глубина проверки: Top-100.
                </Typography>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={busy}>
            Отмена
          </Button>
          <Button type="submit" variant="contained" disabled={busy || loading}>
            Добавить
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
